import * as React from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import {
  Grid,
  GridColumn as Column,
} from "@progress/kendo-react-grid";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Upload } from "@progress/kendo-react-upload";

import ErrorBanner from "../../../components/ErrorBanner";
import {
  useMutateValidateBrokerImport,
  useQueryGetBrokerImportBatch,
  useMutateApplyBrokerImport,
} from "../../../data/services/brokerUserService";
import { SERVICE_URLS } from "../../../data/helpers/serviceUrls";

const STATUS_COLORS = {
  Valid: "#2e7d32",
  Applied: "#2e7d32",
  Invalid: "#c62828",
  Failed: "#c62828",
  Skipped: "#757575",
};

const OPERATION_COLORS = {
  Create: "#2e7d32",
  Update: "#1976d2",
};

const StatusCell = (props) => {
  const status = props.dataItem.status;
  const color = STATUS_COLORS[status] || "#333";
  const detail = props.dataItem.errors?.join("; ") || props.dataItem.appliedError;
  return (
    <td title={detail || ""}>
      <span style={{ color, fontWeight: 600 }}>{status}</span>
      {detail && (
        <div style={{ fontSize: 12, color: "#666" }}>{detail}</div>
      )}
    </td>
  );
};

const OperationCell = (props) => {
  const operation = props.dataItem.operation || "Create";
  const color = OPERATION_COLORS[operation] || "#333";
  return (
    <td>
      <span style={{ color, fontWeight: 600 }}>{operation}</span>
    </td>
  );
};

const BrokerImportModal = ({ onClose }) => {
  const [files, setFiles] = React.useState([]);
  const [batchId, setBatchId] = React.useState(null);
  const [selectedTab, setSelectedTab] = React.useState(0);

  const validateHandler = useMutateValidateBrokerImport();
  const applyHandler = useMutateApplyBrokerImport();

  // Once we have a batchId, poll while status is Applying so per-row progress updates live.
  const {
    data: batchResponse,
    isLoading: batchLoading,
  } = useQueryGetBrokerImportBatch(batchId, { enabled: !!batchId });

  // Prefer live batch data once we have it; fall back to the validate mutation response
  // for the first render before the initial poll returns.
  const preview =
    batchResponse?.data || validateHandler.data?.data || null;
  const status = preview?.status;

  const isApplying = status === "Applying";
  const isComplete = status === "Complete" || status === "Failed";

  const handleUpload = async () => {
    if (files.length === 0) return;
    const raw = files[0].getRawFile();
    try {
      const result = await validateHandler.mutateAsync(raw);
      if (result?.data?.brokerImportBatchId) {
        setBatchId(result.data.brokerImportBatchId);
      }
    } catch {
      // Error is surfaced via ErrorBanner through validateHandler.isError
    }
  };

  const handleApply = async () => {
    if (!batchId) return;
    try {
      await applyHandler.mutateAsync(batchId);
    } catch {
      // Error is surfaced via ErrorBanner through applyHandler.isError
    }
  };

  const applyDisabled =
    !preview ||
    isApplying ||
    isComplete ||
    (preview.invalidBrokers ?? 0) > 0 ||
    (preview.validBrokers ?? 0) === 0;

  const brokers = preview?.brokers || [];
  const assignments = preview?.assignments || [];

  const appliedCount = preview?.appliedBrokers ?? 0;
  const failedCount = preview?.failedBrokers ?? 0;
  const total = preview?.validBrokers ?? 0;
  const progressPct = total > 0 ? Math.min(100, Math.round(((appliedCount + failedCount) / total) * 100)) : 0;

  return (
    <Dialog title={"Import Broker Users"} onClose={onClose} width={900}>
      <div style={{ minHeight: 400 }}>
        <ErrorBanner handler={validateHandler} />
        <ErrorBanner handler={applyHandler} />

        {!preview && (
          <div>
            <p>
              Download the template, fill in brokers + assignments, then upload the file to preview
              the changes before applying them.
            </p>
            <p>
              <a
                href={SERVICE_URLS.BROKER_IMPORT_TEMPLATE}
                download="Prestige Broker Import Template.xlsx"
              >
                Download Import Template
              </a>
            </p>
            <div style={{ marginTop: 15 }}>
              <Upload
                autoUpload={false}
                showActionButtons={false}
                multiple={false}
                restrictions={{ allowedExtensions: [".xlsx"] }}
                files={files}
                onAdd={(e) => setFiles(e.newState)}
                onRemove={(e) => setFiles(e.newState)}
              />
            </div>
            <div style={{ marginTop: 15 }}>
              <Button
                themeColor="primary"
                disabled={files.length === 0 || validateHandler.isLoading}
                onClick={handleUpload}
              >
                {validateHandler.isLoading ? "Validating..." : "Upload & Validate"}
              </Button>
            </div>
          </div>
        )}

        {preview && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
              <div>
                <strong>File:</strong> {preview.fileName}
                <span style={{ marginLeft: 20 }}>
                  <strong>Status:</strong> {status}
                </span>
              </div>
              <div>
                <span style={{ color: "#2e7d32", marginRight: 15 }}>
                  Valid: {preview.validBrokers ?? 0}
                </span>
                <span style={{ color: "#c62828", marginRight: 15 }}>
                  Invalid: {preview.invalidBrokers ?? 0}
                </span>
                <span style={{ color: OPERATION_COLORS.Create, marginRight: 15 }}>
                  Create: {preview.createBrokers ?? 0}
                </span>
                <span style={{ color: OPERATION_COLORS.Update, marginRight: 15 }}>
                  Update: {preview.updateBrokers ?? 0}
                </span>
                <span>Total: {preview.totalBrokers ?? 0}</span>
              </div>
            </div>

            {(isApplying || isComplete) && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>
                    Applied: {appliedCount} / {total}
                    {failedCount > 0 && `  (${failedCount} failed)`}
                  </span>
                  <span>{progressPct}%</span>
                </div>
                <div
                  style={{
                    height: 8,
                    background: "#eee",
                    borderRadius: 4,
                    overflow: "hidden",
                    marginTop: 4,
                  }}
                >
                  <div
                    style={{
                      width: `${progressPct}%`,
                      height: "100%",
                      background: failedCount > 0 && !isApplying ? "#c62828" : "#2e7d32",
                    }}
                  />
                </div>
                {isApplying && (
                  <div style={{ marginTop: 6 }}>
                    <Loader size="small" /> Applying...
                  </div>
                )}
              </div>
            )}

            <TabStrip
              selected={selectedTab}
              onSelect={(e) => setSelectedTab(e.selected)}
            >
              <TabStripTab title={`Brokers (${brokers.length})`}>
                <Grid data={brokers} style={{ maxHeight: 350 }}>
                  <Column field="rowNumber" title="Row" width="70" />
                  <Column field="userEmail" title="Email" />
                  <Column field="firstName" title="First Name" width="140" />
                  <Column field="lastName" title="Last Name" width="140" />
                  <Column field="userType" title="Type" width="100" />
                  <Column field="operation" title="Operation" cell={OperationCell} width="110" />
                  <Column field="status" title="Status" cell={StatusCell} width="180" />
                </Grid>
              </TabStripTab>
              <TabStripTab title={`Assignments (${assignments.length})`}>
                <Grid data={assignments} style={{ maxHeight: 350 }}>
                  <Column field="rowNumber" title="Row" width="70" />
                  <Column field="userEmail" title="Email" />
                  <Column field="clientId" title="Client Id" width="120" />
                  <Column field="assignmentRole" title="Role" width="120" />
                  <Column field="status" title="Status" cell={StatusCell} width="180" />
                </Grid>
              </TabStripTab>
            </TabStrip>

            {batchLoading && !batchResponse && (
              <div style={{ marginTop: 8 }}>
                <Loader size="small" /> Refreshing...
              </div>
            )}
          </div>
        )}
      </div>

      <DialogActionsBar layout="end">
        <Button onClick={onClose}>{isComplete ? "Close" : "Cancel"}</Button>
        {preview && !isComplete && (
          <Button
            themeColor="primary"
            disabled={applyDisabled || applyHandler.isLoading}
            onClick={handleApply}
          >
            {applyHandler.isLoading || isApplying ? "Applying..." : "Apply"}
          </Button>
        )}
      </DialogActionsBar>
    </Dialog>
  );
};

export default BrokerImportModal;
